import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Plus, FileText, Home, CalendarClock, Trash2, ChevronRight, Loader2, Banknote, Sparkles,
} from 'lucide-react';
import { STATUS_CONFIG, STATUS_ORDER, fmtMoney } from './mortgageConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${cfg.color}`} data-testid={`status-badge-${status}`}>
      {cfg.label}
    </span>
  );
};

export const MortgageDashboard = ({ agent, isAdmin = false, onOpen, onNew }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [loadingSample, setLoadingSample] = useState(false);

  const loadSample = async () => {
    setLoadingSample(true);
    try {
      const r = await fetch(`${API_URL}/api/mortgage/files/sample?agent_id=${encodeURIComponent(agent?.id || '')}&agent_name=${encodeURIComponent(agent?.name || 'Agent')}`, { method: 'POST' });
      if (r.ok) { const d = await r.json(); onOpen(d.id); }
      else { alert('Could not create sample file.'); }
    } catch (e) { alert('Could not create sample file: ' + e.message); } finally { setLoadingSample(false); }
  };

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (agent?.id) params.set('agent_id', agent.id);
      if (isAdmin) params.set('is_admin', 'true');
      if (statusFilter) params.set('status', statusFilter);
      const r = await fetch(`${API_URL}/api/mortgage/files?${params.toString()}`);
      if (r.ok) setFiles(await r.json());
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [agent, isAdmin, statusFilter]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this mortgage file? This cannot be undone.')) return;
    await fetch(`${API_URL}/api/mortgage/files/${id}?agent_id=${encodeURIComponent(agent?.id || '')}&agent_name=${encodeURIComponent(agent?.name || 'Agent')}`, { method: 'DELETE' });
    fetchFiles();
  };

  const isUrgent = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const days = (d - new Date()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 7;
  };

  return (
    <div className="space-y-5" data-testid="mortgage-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#0A2A43] flex items-center gap-2">
            <Banknote className="h-6 w-6 text-[#0A2A43]" /> Mortgage App Workflow
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            AI-assisted submission note generator. Capture borrower, mortgage &amp; property details,
            compute qualifying income, and generate a lender submission note.
          </p>
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 inline-block">
            ⚠ Preliminary broker-side assessment only — does not replace lender underwriting, insurer review, or broker judgment. All output is reviewable and editable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={loadSample} disabled={loadingSample} variant="outline" className="border-[#0A2A43] text-[#0A2A43]" data-testid="load-sample-file-btn">
            {loadingSample ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} Load Sample File
          </Button>
          <Button onClick={onNew} className="bg-[#0A2A43] hover:bg-[#0d3a5c] text-white" data-testid="new-mortgage-file-btn">
            <Plus className="h-4 w-4 mr-1" /> New Mortgage File
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setStatusFilter('')}
          className={`text-xs px-3 py-1.5 rounded-full border ${!statusFilter ? 'bg-[#0A2A43] text-white border-[#0A2A43]' : 'bg-white text-gray-600'}`}
          data-testid="filter-all"
        >All</button>
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === s ? 'bg-[#0A2A43] text-white border-[#0A2A43]' : 'bg-white text-gray-600'}`}
            data-testid={`filter-${s}`}
          >{STATUS_CONFIG[s].label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0A2A43]" /></div>
      ) : files.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-gray-500">
          <FileText className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          No mortgage files yet. Click <b>New Mortgage File</b> to start.
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="mortgage-file-list">
          {files.map((f) => (
            <Card
              key={f.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onOpen(f.id)}
              data-testid={`mortgage-file-${f.id}`}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#0A2A43] truncate">{f.client_name}</span>
                    <StatusBadge status={f.status} />
                    {f.transaction_type && <span className="text-xs text-gray-500">· {f.transaction_type}</span>}
                  </div>
                  <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-1">
                    <Home className="h-3 w-3" /> {f.property_address || 'No address'} {f.total_qualifying_annual ? `· ${fmtMoney(f.total_qualifying_annual)} qualifying` : ''}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap text-[11px]">
                    {f.financing_condition_deadline && (
                      <span className={`flex items-center gap-1 ${isUrgent(f.financing_condition_deadline) ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
                        <CalendarClock className="h-3 w-3" /> Condition: {f.financing_condition_deadline}{isUrgent(f.financing_condition_deadline) ? ' (urgent)' : ''}
                      </span>
                    )}
                    {f.closing_date && <span className="text-gray-500">Closing: {f.closing_date}</span>}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={(e) => remove(e, f.id)} data-testid={`delete-mortgage-file-${f.id}`}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
                <ChevronRight className="h-5 w-5 text-gray-300" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MortgageDashboard;
